function escapePdfText(value: string): string {
  return String(value || "")
    .replace(/[^\x20-\x7E]/g, "?")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function wrapLine(text: string, maxChars = 92): string[] {
  const raw = String(text || "").trim();
  if (!raw) return [""];
  const words = raw.split(/\s+/);
  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    if (!current) {
      current = word;
      return;
    }
    if (`${current} ${word}`.length <= maxChars) {
      current = `${current} ${word}`;
      return;
    }
    lines.push(current);
    current = word;
  });

  if (current) {
    lines.push(current);
  }

  return lines;
}

function buildPdfDocument(lines: string[]): Uint8Array {
  const pageWidth = 612;
  const pageHeight = 792;
  const left = 48;
  const top = 744;
  const lineHeight = 16;
  const linesPerPage = 42;
  const chunks: string[][] = [];

  for (let index = 0; index < lines.length; index += linesPerPage) {
    chunks.push(lines.slice(index, index + linesPerPage));
  }

  if (chunks.length === 0) {
    chunks.push([""]);
  }

  const objects: string[] = [];
  objects.push("<< /Type /Catalog /Pages 2 0 R >>");

  const pageObjectIds = chunks.map((_chunk, index) => 3 + index * 2);
  const contentObjectIds = chunks.map((_chunk, index) => 4 + index * 2);

  objects.push(`<< /Type /Pages /Count ${chunks.length} /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(" ")}] >>`);

  chunks.forEach((chunk, index) => {
    const pageObjectId = pageObjectIds[index];
    const contentObjectId = contentObjectIds[index];
    void pageObjectId;
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> >> >> /Contents ${contentObjectId} 0 R >>`
    );

    const body = [
      "BT",
      "/F1 10 Tf",
      `${left} ${top} Td`,
    ];

    chunk.forEach((line, lineIndex) => {
      if (lineIndex > 0) {
        body.push(`0 -${lineHeight} Td`);
      }
      body.push(`(${escapePdfText(line)}) Tj`);
    });

    body.push("ET");
    const stream = body.join("\n");
    objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });

  let output = "%PDF-1.4\n";
  const offsets = [0];

  objects.forEach((object, index) => {
    offsets.push(output.length);
    output += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefStart = output.length;
  output += `xref\n0 ${objects.length + 1}\n`;
  output += "0000000000 65535 f \n";
  offsets.slice(1).forEach((offset) => {
    output += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  output += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

  return new TextEncoder().encode(output);
}

export function downloadSessionPdf(input: {
  filename: string;
  title: string;
  generatedAt: string;
  summary: Array<{ label: string; value: string | number }>;
  rows: Array<Record<string, string | number | null | undefined>>;
}): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const lines: string[] = [];
  lines.push(input.title);
  lines.push(`Generated: ${input.generatedAt}`);
  lines.push("");
  lines.push("Summary");
  input.summary.forEach((item) => {
    lines.push(`${item.label}: ${item.value}`);
  });
  lines.push("");
  lines.push("Session Items");

  input.rows.forEach((row, index) => {
    lines.push(`Item ${index + 1}`);
    Object.entries(row).forEach(([key, value]) => {
      wrapLine(`${key}: ${value == null || value === "" ? "-" : value}`).forEach((line) => {
        lines.push(`  ${line}`);
      });
    });
    lines.push("");
  });

  const bytes = buildPdfDocument(lines);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = input.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}
